import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import { jwtDecode } from 'jwt-decode'
import { ACCESS_TOKEN, API_BASE_URL, REFRESH_TOKEN } from '../../assets/constants'
import { clearPinBrowserFlags } from '../../util/pinBrowserFlags'
import { RefreshTokenResponse, SessionRequest } from '../../proto/generated/identity'
import { buildDeviceInfoSummary, getDeviceId } from '../../util/deviceIdentity'
import { clearLoginSessionWindow, setLoginSessionWindow24h } from '../../util/loginSessionWindow'
import { isAccessTokenValid } from '../../util/authentication'
import { version as CLIENT_VERSION } from '../../../package.json'

/** Refresh ~90s before access JWT expiry (server skew). */
const REFRESH_BEFORE_EXPIRY_SEC = 90

let proactiveTimer: ReturnType<typeof setTimeout> | null = null
let inFlightRefresh: Promise<boolean> | null = null
const accessTokenRefreshListeners = new Set<() => void>()

function clearProactiveTimer() {
	if (proactiveTimer !== null) {
		clearTimeout(proactiveTimer)
		proactiveTimer = null
	}
}

export function scheduleProactiveAccessTokenRefresh() {
	clearProactiveTimer()
	const access = localStorage.getItem(ACCESS_TOKEN)
	const refresh = localStorage.getItem(REFRESH_TOKEN)
	if (!refresh) return
	if (!access) {
		void refreshAccessToken()
		return
	}
	try {
		const { exp } = jwtDecode<{ exp?: number }>(access)
		if (!exp) {
			void refreshAccessToken()
			return
		}
		const fireAtMs = (exp - REFRESH_BEFORE_EXPIRY_SEC) * 1000 - Date.now()
		if (fireAtMs <= 0) {
			void refreshAccessToken()
			return
		}
		proactiveTimer = setTimeout(() => {
			void refreshAccessToken()
		}, fireAtMs)
	} catch {
		void refreshAccessToken()
	}
}

function notifyAccessTokenRefreshed() {
	accessTokenRefreshListeners.forEach((listener) => listener())
}

function applyRefreshResponse(decoded: RefreshTokenResponse) {
	localStorage.setItem(ACCESS_TOKEN, decoded.accessToken)
	scheduleProactiveAccessTokenRefresh()
	notifyAccessTokenRefreshed()
}

export function subscribeAccessTokenRefresh(listener: () => void): () => void {
	accessTokenRefreshListeners.add(listener)
	return () => accessTokenRefreshListeners.delete(listener)
}

export async function ensureValidAccessToken(): Promise<string | null> {
	const access = localStorage.getItem(ACCESS_TOKEN)
	if (access && isAccessTokenValid()) {
		return access
	}
	if (!localStorage.getItem(REFRESH_TOKEN)) {
		return null
	}
	const ok = await refreshAccessToken()
	if (!ok) {
		return null
	}
	return localStorage.getItem(ACCESS_TOKEN)
}

export function persistAuthFromLoginResponse(response: { accessToken: string; refreshToken?: string }) {
	localStorage.setItem(ACCESS_TOKEN, response.accessToken)
	if (response.refreshToken) {
		localStorage.setItem(REFRESH_TOKEN, response.refreshToken)
	}
	setLoginSessionWindow24h()
	scheduleProactiveAccessTokenRefresh()
	notifyAccessTokenRefreshed()
}

export function clearAuthSession() {
	clearProactiveTimer()
	localStorage.removeItem(ACCESS_TOKEN)
	localStorage.removeItem(REFRESH_TOKEN)
	clearPinBrowserFlags()
	clearLoginSessionWindow()
	inFlightRefresh = null
	accessTokenRefreshListeners.clear()
}

/**
 * Uses refresh token + same device id/info as login. Plain axios to avoid interceptor loops.
 */
export async function refreshAccessToken(): Promise<boolean> {
	if (inFlightRefresh) return inFlightRefresh

	const refresh = localStorage.getItem(REFRESH_TOKEN)
	if (!refresh) return false

	inFlightRefresh = (async () => {
		try {
			const body = SessionRequest.fromPartial({
				refreshToken: refresh,
				deviceId: getDeviceId(),
				deviceInfo: await buildDeviceInfoSummary(),
			})
			const res = await axios.post(`${API_BASE_URL}/auth/refresh`, JSON.stringify(SessionRequest.toJSON(body)), {
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/x-protobuf',
					'X-Client-Version': CLIENT_VERSION,
				},
				responseType: 'arraybuffer',
				validateStatus: (s) => s === 200,
			})
			const decoded = RefreshTokenResponse.decode(new Uint8Array(res.data))
			if (!decoded.accessToken) return false
			applyRefreshResponse(decoded)
			return true
		} catch {
			return false
		} finally {
			inFlightRefresh = null
		}
	})()

	return inFlightRefresh
}

const AUTH_PATH_SKIP_REFRESH = ['/auth/login', '/auth/refresh', '/auth/oauth/token/validate', '/auth/login-url']

function shouldAttemptRefreshOn401(fullUrl: string): boolean {
	return !AUTH_PATH_SKIP_REFRESH.some((p) => fullUrl.includes(p))
}

export async function attempt401RefreshRetry(
	client: AxiosInstance,
	error: any
): Promise<AxiosResponse | null> {
	const cfg = error?.config
	if (error?.response?.status !== 401 || !cfg || cfg._authRefreshTried) {
		return null
	}
	const fullUrl = String((cfg.baseURL ?? '') + (cfg.url ?? ''))
	if (!shouldAttemptRefreshOn401(fullUrl)) {
		return null
	}
	cfg._authRefreshTried = true
	const ok = await refreshAccessToken()
	if (!ok) {
		return null
	}
	const token = localStorage.getItem(ACCESS_TOKEN)
	if (!token) {
		return null
	}
	cfg.headers = { ...cfg.headers, Authorization: `Bearer ${token}` }
	return client.request(cfg)
}
